import type { Workitem } from '@rhythmic/db/types'
import type { WorkitemSearchFilters } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { createHooks } from 'hookable'
import type { Ref } from 'vue'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import { useWorkitemsByTeamId } from '../../queries'
import useBacklogContextOnContextChanged from './events/useBacklogContext.onContextChanged'
import useBacklogContextOnSearchFiltersChanged from './events/useBacklogContext.onSearchFiltersChanged'
import useBacklogContextOnTeamSlugChanged from './events/useBacklogContext.onTeamSlugChanged'
import useBacklogContextOnWorkspaceSlugChanged from './events/useBacklogContext.onWorkspaceSlugChanged'
import type { BacklogContextEvents } from './useBacklogContext.events.schema'

interface BacklogContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface InitBacklogContextQueriesParams {
  logger: BacklogContextLoggerLike
  teamId: Readonly<Ref<string | undefined>>
  workspaceId: Readonly<Ref<string | undefined>>
}

/**
 * Backlog context query orchestrator with explicit, hook-driven updates.
 *
 * Route params are synced into the backlog store by `backlog-context-router.client`,
 * which clears backlog queries on slug changes to avoid stale displays. Separately,
 * resolved identifiers (team/workspace ids) are observed and emitted via `ContextChanged`,
 * while user-controlled search filters are emitted via `SearchFiltersChanged`.
 */
export default function useBacklogContextQueries() {
  type BacklogContextHookMap = {
    [K in keyof BacklogContextEvents]: (event: BacklogContextEvents[K]) => void | Promise<void>
  }

  const hooks = markRaw(createHooks<BacklogContextHookMap>())

  const workitemsQuery = shallowRef<UseLiveQueryReturn<Workitem> | undefined>(undefined)
  const workitemsQueryKey = ref<string | undefined>(undefined)

  const searchFilters = ref<WorkitemSearchFilters | undefined>(undefined)

  const workitems = computed<Workitem[]>(() => workitemsQuery.value?.data.value ?? [])
  const isLoadingWorkitems = computed<boolean>(() => workitemsQuery.value?.isLoading.value ?? false)

  let hasInitialized = false

  function clearBacklogScopedQueries() {
    workitemsQuery.value = undefined
  }

  function init({ logger, teamId, workspaceId }: InitBacklogContextQueriesParams) {
    if (hasInitialized) return

    hasInitialized = true

    useBacklogContextOnWorkspaceSlugChanged({
      hooks,
      logger,
      clearBacklogScopedQueries,
      workitemsQueryKey,
    })

    useBacklogContextOnTeamSlugChanged({
      hooks,
      logger,
      clearBacklogScopedQueries,
      workitemsQueryKey,
    })

    useBacklogContextOnContextChanged({
      hooks,
      logger,
      workitemsQuery,
      clearBacklogScopedQueries,
      createWorkitemsQuery: (id, filters) => useWorkitemsByTeamId(id, filters),
      getFilters: () => searchFilters.value,
      workitemsQueryKey,
    })

    useBacklogContextOnSearchFiltersChanged({
      hooks,
      logger,
      workitemsQuery,
      clearBacklogScopedQueries,
      createWorkitemsQuery: (id, filters) => useWorkitemsByTeamId(id, filters),
      workitemsQueryKey,
    })

    watch(
      [teamId, workspaceId],
      ([nextTeamId, nextWorkspaceId], [previousTeamId, previousWorkspaceId]) => {
        void emitContextChanged({
          teamId: nextTeamId,
          previousTeamId,
          workspaceId: nextWorkspaceId,
          previousWorkspaceId,
        })
      },
      { immediate: true },
    )
  }

  function setSearchFilters(
    filters: WorkitemSearchFilters | undefined,
    meta: { teamId: string | undefined; workspaceId: string | undefined },
  ) {
    const previousFilters = searchFilters.value
    searchFilters.value = filters

    void emitSearchFiltersChanged({
      teamId: meta.teamId,
      workspaceId: meta.workspaceId,
      filters: searchFilters.value,
      previousFilters,
    })
  }

  function onWorkspaceSlugChanged(handler: BacklogContextHookMap['WorkspaceSlugChanged']) {
    return hooks.hook('WorkspaceSlugChanged', handler)
  }

  function onTeamSlugChanged(handler: BacklogContextHookMap['TeamSlugChanged']) {
    return hooks.hook('TeamSlugChanged', handler)
  }

  function onContextChanged(handler: BacklogContextHookMap['ContextChanged']) {
    return hooks.hook('ContextChanged', handler)
  }

  function onSearchFiltersChanged(handler: BacklogContextHookMap['SearchFiltersChanged']) {
    return hooks.hook('SearchFiltersChanged', handler)
  }

  async function emitWorkspaceSlugChanged(event: BacklogContextEvents['WorkspaceSlugChanged']) {
    return hooks.callHook('WorkspaceSlugChanged', event)
  }

  async function emitTeamSlugChanged(event: BacklogContextEvents['TeamSlugChanged']) {
    return hooks.callHook('TeamSlugChanged', event)
  }

  async function emitContextChanged(event: BacklogContextEvents['ContextChanged']) {
    return hooks.callHook('ContextChanged', event)
  }

  async function emitSearchFiltersChanged(event: BacklogContextEvents['SearchFiltersChanged']) {
    return hooks.callHook('SearchFiltersChanged', event)
  }

  return {
    init,
    hooks,
    setSearchFilters,
    onWorkspaceSlugChanged,
    onTeamSlugChanged,
    onContextChanged,
    onSearchFiltersChanged,
    emitWorkspaceSlugChanged,
    emitTeamSlugChanged,
    emitContextChanged,
    emitSearchFiltersChanged,
    workitemsQuery,
    workitems,
    isLoadingWorkitems,
    searchFilters,
  }
}
